/**
 * RudderStack Analytics Class
 *
 * Provides a clean interface for tracking events with RudderStack.
 * Uses queueing to ensure events are tracked in the correct order.
 */

class RudderstackAnalytics {
  constructor() {
    this.isReady = false;
    this.eventQueue = [];
    this.init();
  }

  init() {
    if (typeof window.rudderanalytics === "undefined") {
      return;
    }

    window.rudderanalytics.ready(() => {
      this.isReady = true;
      
      window.rudderanalytics.reset();

      this.processQueue();
    });
  }

  processQueue() {
    while (this.eventQueue.length > 0) {
      const { method, args } = this.eventQueue.shift();
      this[method](...args);
    }
  }

  queueEvent(method, args) {
    this.eventQueue.push({ method, args });
  }

  /**
   * Track a page view
   * @param {string} pageName - Name of the page
   * @param {Object} properties - Additional properties
   */
  page(pageName = null, properties = {}) {
    if (!this.isReady) {
      this.queueEvent("page", [pageName, properties]);
      return;
    }

    try {
      if (pageName) {
        window.rudderanalytics.page(pageName, properties);
      } else {
        window.rudderanalytics.page(properties);
      }
    } catch (error) {
      console.error("Error tracking page:", error);
    }
  }

  /**
   * Track a custom event
   * @param {string} eventName - Name of the event
   * @param {Object} properties - Event properties
   */
  track(eventName, properties = {}) {
    if (!this.isReady) {
      this.queueEvent("track", [eventName, properties]);
      return;
    }

    if (!eventName) {
      return;
    }

    try {
      window.rudderanalytics.track(eventName, properties);
    } catch (error) {
      console.error("Error tracking event:", error);
    }
  }

  /**
   * Track sign up events
   * @param {string} ctaText - Text of the cta
   * @param {Object} additionalProperties - Additional properties
   */
  trackSignUpCtaClicked({ ctaText, additionalProperties = {} } = {}) {
    this.track("signup cta clicked", {
      cta_text: ctaText,
      page_url: window.location.href,
      ...additionalProperties,
    });
  }

  /**
   * Track log in cta clicked
   * @param {Object} additionalProperties - Additional properties
   */
  trackLoginCtaClicked({ additionalProperties = {} } = {}) {
    this.track("log in cta clicked", {
      page_url: window.location.href,
      ...additionalProperties,
    });
  }

  /**
   * Track forgot company login clicked
   * @param {Object} additionalProperties - Additional properties
   */
  trackForgotCompanyLoginClicked({ additionalProperties = {} } = {}) {
    this.track("forgot company login clicked", {
      page_url: window.location.href,
      ...additionalProperties,
    });
  }

  /**
   * Track logo clicked
   * @param {Object} additionalProperties - Additional properties
   */
  trackLogoClicked({ additionalProperties = {} } = {}) {
    this.track("logo clicked", {
      page_url: window.location.href,
      ...additionalProperties,
    });
  }

  /**
   * Track download accessibility report clicked
   * @param {Object} additionalProperties - Additional properties
   */
  trackDownloadAccessibilityReportClicked({ additionalProperties = {} } = {}) {
    this.track("download accessibility report clicked", {
      page_url: window.location.href,
      ...additionalProperties,
    });
  }

  /**
   * Get the anonymous ID from Rudderstack
   * @returns {string|null} The anonymous ID or null if not available
   */
  getAnonymousId() {
    if (!this.isReady || typeof window.rudderanalytics === "undefined") {
      return null;
    }

    try {
      return window.rudderanalytics.getAnonymousId();
    } catch (error) {
      console.error("Error getting anonymous ID:", error);
      return null;
    }
  }
}

const rudderstack = new RudderstackAnalytics();

if (typeof window !== "undefined") {
  window.rudderstack = rudderstack;
}

export { rudderstack };
export default rudderstack;
